import React, { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import { CheckCircle2, Clock, Target } from 'lucide-react';
const domainDetails = [{
  id: 'focus-machine-intelligence',
  title: 'AI-to-Value Operating Model',
  description: 'Align leadership around a clear AI value thesis tied to customer and financial outcomes. Establish governance, decision rights, and a prioritized portfolio roadmap that moves the organization from experimentation to enterprise value.',
  deliverables: 'AI value thesis, success metrics, portfolio roadmap, and operating and governance model.',
  format: '1–2 day executive workshop with cross-functional leadership participation.'
}, {
  id: 'focus-pmi',
  title: 'Post-Merger & Enterprise Integration Sprint',
  description: 'Accelerate value capture following acquisitions, carve-outs, or major reorganizations by aligning leaders on priorities, decision rights, and integration cadence.',
  deliverables: '30/60/90-day integration roadmap, synergy and KPI framework, and governance and escalation model.',
  format: '1–2 day leadership working session.'
}, {
  id: 'focus-governance',
  title: 'Transformation Governance & Fiduciary Clarity',
  description: 'Provide boards and executive teams with clear governance structures and decision frameworks to manage risk, regulatory exposure, AI oversight, and cost pressure while protecting long-term value.',
  deliverables: 'Governance charter, oversight and risk dashboards, and strategic cost and investment guardrails.',
  format: 'Board or C-suite focused workshop.'
}, {
  id: 'focus-cohesion',
  title: 'Cultural Fault Lines & Leadership Alignment',
  description: 'Surface cultural, political, and leadership misalignments that undermine execution and align leaders around what is changing, what is not, and how success will be measured.',
  deliverables: 'Stakeholder and cultural risk map, leadership alignment actions, and a clear executive change narrative.',
  format: 'Executive offsite or transformation kickoff.'
}, {
  id: 'focus-velocity',
  title: 'Executive Operating Rhythm & Decision Velocity',
  description: 'Install a practical operating rhythm that increases decision speed, accountability, and execution discipline across leadership teams, including hybrid and distributed environments.',
  deliverables: 'Executive sprint cadence, decision and accountability framework, and performance metrics.',
  format: 'Half-day to full-day workshop with immediate adoption.'
}, {
  id: 'focus-rescue',
  title: 'Program Rescue, Reset & Value Recovery',
  description: 'Rapidly stabilize initiatives that are late, losing credibility, or politically entangled by resetting priorities, clarifying ownership, and restoring momentum.',
  deliverables: '10-day triage, reset roadmap, early-win backlog, and risk mitigation plan.',
  format: 'Short, intensive intervention initiated by the CEO or board.'
}];
export function DomainDetails() {
  const location = useLocation();
  useEffect(() => {
    if (location.hash) {
      const element = document.getElementById(location.hash.substring(1));
      if (element) {
        // Add a slight delay to ensure rendering is complete before scrolling
        setTimeout(() => {
          element.scrollIntoView({
            behavior: 'smooth',
            block: 'start'
          });
        }, 100);
      }
    } else {
      window.scrollTo(0, 0);
    }
  }, [location]);
  return <main className="pt-32 pb-24 md:pt-40 md:pb-32" data-id="element-144">
      <div className="max-w-4xl mx-auto px-6 md:px-12" data-id="element-145">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.6
      }} className="mb-16 md:mb-24" data-id="element-146">
          <p className="text-sm font-medium tracking-wider uppercase text-amber mb-4" data-id="element-147">
            Deep Dive
          </p>
          <h1 className="text-4xl md:text-6xl font-serif text-offwhite leading-tight mb-6" data-id="element-148">
            Our Domain Focus Areas
          </h1>
          <p className="text-lg md:text-xl text-offwhite-muted font-light leading-relaxed" data-id="element-149">
            Explore our specialized methodologies designed to turn critical
            inflection points into strategic launchpads for your organization.
          </p>
        </motion.div>

        <div className="space-y-16 md:space-y-24" data-id="element-150">
          {domainDetails.map((domain, index) => <motion.div key={domain.id} id={domain.id} initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true,
          margin: '-100px'
        }} transition={{
          duration: 0.6
        }} className="scroll-mt-32" data-id="element-151">
              <div className="flex items-center gap-4 mb-6" data-id="element-152">
                <div className="w-12 h-12 rounded-full bg-navy-light border border-amber/30 flex items-center justify-center flex-shrink-0 text-amber font-serif text-xl" data-id="element-153">
                  {index + 1}
                </div>
                <h2 className="text-3xl md:text-4xl font-serif text-offwhite leading-tight" data-id="element-154">
                  {domain.title}
                </h2>
              </div>

              <div className="pl-0 md:pl-16" data-id="element-155">
                <p className="text-lg text-offwhite-muted font-light leading-relaxed mb-8" data-id="element-156">
                  {domain.description}
                </p>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-157">
                  <div className="bg-navy-light/40 border border-white/10 rounded-md p-6" data-id="element-158">
                    <div className="flex items-center gap-2 mb-3 text-amber" data-id="element-159">
                      <Target size={18} data-id="element-160" />
                      <h3 className="text-sm font-medium tracking-wider uppercase" data-id="element-161">
                        Deliverables
                      </h3>
                    </div>
                    <p className="text-sm text-offwhite-muted leading-relaxed" data-id="element-162">
                      {domain.deliverables}
                    </p>
                  </div>

                  <div className="bg-navy-light/40 border border-white/10 rounded-md p-6" data-id="element-163">
                    <div className="flex items-center gap-2 mb-3 text-amber" data-id="element-164">
                      <Clock size={18} data-id="element-165" />
                      <h3 className="text-sm font-medium tracking-wider uppercase" data-id="element-166">
                        Typical Format
                      </h3>
                    </div>
                    <p className="text-sm text-offwhite-muted leading-relaxed" data-id="element-167">
                      {domain.format}
                    </p>
                  </div>
                </div>
              </div>
            </motion.div>)}
        </div>
      </div>
    </main>;
}